/**
 * Mint storage operations for Cashero.
 *
 * CRUD helpers for mint-side IndexedDB data using Dexie.
 */

import { db, type StoredMintConfig, type PendingDeposit, type SpentSecret } from './db';
import type { KeysetPrivate } from '@/lib/cashu/keyset';

// ====================================================================
// Mint Config
// ====================================================================

/** Check if mint setup is complete */
export async function isMintSetupComplete(): Promise<boolean> {
  const config = await db.mintConfig.get('config');
  return config?.setupComplete ?? false;
}

/** Get mint config */
export async function getMintConfig(): Promise<StoredMintConfig | undefined> {
  return db.mintConfig.get('config');
}

/** Save mint config */
export async function saveMintConfig(config: StoredMintConfig): Promise<void> {
  await db.mintConfig.put(config);
}

/** Mark mint setup as complete */
export async function completeMintSetup(): Promise<void> {
  const config = await db.mintConfig.get('config');
  if (config) {
    config.setupComplete = true;
    await db.mintConfig.put(config);
  }
}

/** Delete all mint data (factory reset) */
export async function resetMintData(): Promise<void> {
  await db.mintConfig.clear();
  await db.mintKeysets.clear();
  await db.pendingDeposits.clear();
  await db.spentSecrets.clear();
}

// ====================================================================
// Keysets
// ====================================================================

/** Get all private keysets */
export async function getAllKeysets(): Promise<KeysetPrivate[]> {
  return db.mintKeysets.toArray();
}

/** Get active keysets */
export async function getActiveKeysets(): Promise<KeysetPrivate[]> {
  const all = await db.mintKeysets.toArray();
  return all.filter(k => k.active);
}

/** Get a specific keyset by ID */
export async function getKeyset(id: string): Promise<KeysetPrivate | undefined> {
  return db.mintKeysets.get(id);
}

/** Save a keyset */
export async function saveKeyset(keyset: KeysetPrivate): Promise<void> {
  await db.mintKeysets.put(keyset);
}

/** Deactivate a keyset (after rotation) */
export async function deactivateKeyset(id: string): Promise<void> {
  const keyset = await db.mintKeysets.get(id);
  if (keyset) {
    keyset.active = false;
    await db.mintKeysets.put(keyset);
  }
}

// ====================================================================
// Pending Deposits
// ====================================================================

/** Get all pending deposits */
export async function getAllPendingDeposits(): Promise<PendingDeposit[]> {
  return db.pendingDeposits.toArray();
}

/** Get deposits by status */
export async function getDepositsByStatus(status: PendingDeposit['status']): Promise<PendingDeposit[]> {
  return db.pendingDeposits.where('status').equals(status).toArray();
}

/** Get a specific pending deposit */
export async function getPendingDeposit(requestId: string): Promise<PendingDeposit | undefined> {
  return db.pendingDeposits.get(requestId);
}

/** Save a pending deposit */
export async function savePendingDeposit(deposit: PendingDeposit): Promise<void> {
  await db.pendingDeposits.put(deposit);
}

/** Update deposit status and confirmations */
export async function updateDepositStatus(
  requestId: string,
  status: PendingDeposit['status'],
  updates?: Partial<Pick<PendingDeposit, 'confirmations' | 'txHash' | 'detectedAt' | 'confirmedAt'>>,
): Promise<void> {
  const deposit = await db.pendingDeposits.get(requestId);
  if (deposit) {
    Object.assign(deposit, { status, ...updates });
    await db.pendingDeposits.put(deposit);
  }
}

/** Delete completed deposits older than 24 hours */
export async function cleanupOldDeposits(): Promise<void> {
  const cutoff = Date.now() - 86400000;
  const old = await db.pendingDeposits
    .filter(d => d.status === 'done' && (d.confirmedAt ?? 0) < cutoff)
    .toArray();
  for (const d of old) {
    await db.pendingDeposits.delete(d.requestId);
  }
}

// ====================================================================
// Spent Secrets
// ====================================================================

/** Check if a secret hash has been spent */
export async function isSecretSpent(secretHash: string): Promise<boolean> {
  const result = await db.spentSecrets.get(secretHash);
  return !!result;
}

/** Check multiple secret hashes at once */
export async function areSecretsSpent(secretHashes: string[]): Promise<boolean[]> {
  const results = await Promise.all(
    secretHashes.map(hash => db.spentSecrets.get(hash)),
  );
  return results.map(r => !!r);
}

/** Mark a secret hash as spent */
export async function markSecretSpent(secretHash: string, spenderNpub?: string): Promise<void> {
  await db.spentSecrets.put({
    secretHash,
    spentAt: Date.now(),
    spenderNpub,
  });
}

/** Mark multiple secret hashes as spent */
export async function markSecretsSpent(secretHashes: string[], spenderNpub?: string): Promise<void> {
  const now = Date.now();
  const entries: SpentSecret[] = secretHashes.map(hash => ({
    secretHash: hash,
    spentAt: now,
    spenderNpub,
  }));
  await db.spentSecrets.bulkAdd(entries);
}

/** Get all spent secrets */
export async function getAllSpentSecrets(): Promise<SpentSecret[]> {
  return db.spentSecrets.toArray();
}
